'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { CustomCursor } from '@/components/cursor/CustomCursor';
import { CursorProvider } from '@/components/cursor/CursorContext';
import { SmoothScrollProvider } from '@/components/animations/SmoothScrollProvider';

import { GlobalBackground } from '@/components/layout/GlobalBackground';
import { AmbientWing } from '@/components/layout/AmbientWing';
import { AmbientWow } from '@/components/effects/AmbientWow';
import WebWidgetScript from '@/components/WebWidget';
import { localeFromPathname } from '@/lib/i18n/config';
import { LocaleProvider, useLocale } from '@/lib/i18n/LocaleContext';

function LocaleHtmlSync() {
  const { locale, dir } = useLocale();
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);
  return null;
}

function MainLayoutContent({ children }: { children: React.ReactNode }) {
  const { dict, dir, locale } = useLocale();
  return (
    <CursorProvider>
      <LocaleHtmlSync />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[10000] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-black focus:shadow-lg focus:outline-2 focus:outline-offset-2 focus:outline-[#7CFFB2]"
      >
        {dict.common.skipToMainContent}
      </a>
      <CustomCursor />
      <GlobalBackground />
      <AmbientWing />
      <AmbientWow />
      <Header />
      <SmoothScrollProvider>
        <main id="main" className="min-h-screen" dir={dir} lang={locale}>
          {children}
        </main>
        <Footer />
      </SmoothScrollProvider>
      <WebWidgetScript />
    </CursorProvider>
  );
}

export default function MainLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const locale = localeFromPathname(pathname);

  // The application flow is a focused, chrome-less page in every language
  // (/apply, /ar/apply, /ckb/apply): no header, footer or cursor, but it still
  // needs the locale context and the html lang/dir sync.
  if (/^\/(?:(?:ar|ckb)\/)?apply\/?$/.test(pathname)) {
    return (
      <LocaleProvider locale={locale}>
        <LocaleHtmlSync />
        <main id="main">{children}</main>
      </LocaleProvider>
    );
  }

  return (
    <LocaleProvider locale={locale}>
      <MainLayoutContent>{children}</MainLayoutContent>
    </LocaleProvider>
  );
}
